import {
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA,
} from "@components/Domain";

import isJointInvestment from "./isJointInvestment";
import isEntityInvestment from "./isEntityInvestment";
import isIndividualInvestment from "./isIndividualInvestment";

type ProfileEntity = {
  einNumber?: string;
  beneficialOwnerSsnNumber?: string;
  beneficialOwnerEinNumber?: string;
  beneficialOwnerEntityType?: string;
  isDisregardedForTaxPurposes?: boolean;
  beneficialOwnerCustodianEinNumber?: string;
};

type ProfileWithSsn = {
  ssnNumber?: string;
};

type Investment = {
  profileType?: string;
  profileJoint?: ProfileWithSsn;
  profileEntity?: ProfileEntity;
  profileIndividual?: ProfileWithSsn;
};

const hasEntityTin = (profileEntity: ProfileEntity): boolean => {
  const {
    einNumber,
    beneficialOwnerSsnNumber,
    beneficialOwnerEinNumber,
    beneficialOwnerEntityType,
    isDisregardedForTaxPurposes,
    beneficialOwnerCustodianEinNumber,
  } = profileEntity;

  const hasEin = !!einNumber;

  if (!isDisregardedForTaxPurposes) {
    return hasEin;
  }

  const isIndividualType =
    beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL;

  if (isIndividualType) {
    const hasOwnerSsn = !!beneficialOwnerSsnNumber;

    return hasEin && hasOwnerSsn;
  }

  const isIra =
    beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;

  if (isIra) {
    const hasCustodianEin = !!beneficialOwnerCustodianEinNumber;

    return hasEin && hasCustodianEin;
  }

  const hasOwnerEin = !!beneficialOwnerEinNumber;

  return hasEin && hasOwnerEin;
};

const hasTin = (investment: Investment): boolean => {
  const isJoint = isJointInvestment(investment);
  const isEntity = isEntityInvestment(investment);
  const isIndividual = isIndividualInvestment(investment);

  const {
    profileJoint = {},
    profileEntity = {},
    profileIndividual = {},
  } = investment;

  if (isEntity) {
    const hasEin = hasEntityTin(profileEntity);

    return hasEin;
  }

  if (isIndividual) {
    const { ssnNumber } = profileIndividual;

    const hasSsn = !!ssnNumber;

    return hasSsn;
  }

  if (isJoint) {
    const { ssnNumber } = profileJoint;

    const hasSsn = !!ssnNumber;

    return hasSsn;
  }

  return false;
};

export default hasTin;
